// Turns a canvas tap/hover point into concrete game info. Pure glue between
// the pointer layer and the engine: `resolveDiscardFromTap` gates on the
// local seat actually being on turn to discard (so taps in any other phase/
// turn are inert, per the free-play input rules); `findHoveredTile` answers
// "what tile, if any, sits under this point" across every face-up tile on
// the table — local hand, any seat's melds, any seat's discards — driving
// the hover tooltip + pointer cursor (see `ui/TileHoverCursor`).

import { SEATS, sortTiles, type Seat, type Tile } from '../game/types.js';
import type { MahjongState } from '../game/GameState.js';
import { seatPosition, type LayoutConfig, type SeatZone } from '../layout/types.js';
import { hitTestDiscardPile, hitTestHandTile, hitTestMeldStrip, type Point } from '../input/hitTest.js';
import { pondWrapFrom, splitPondCell } from '../render/tileLayout.js';
import type { Viewport } from '../render/Viewport.js';
import {
  DISCARD_TILE_GAP_PX,
  MELD_END_INSET_PX,
  MELD_STRIP_GAP_PX,
  MELD_STRIP_THICKNESS_PX,
  MELD_TILE_GAP_PX,
  MELD_TILE_SCALE,
  TILE_HEIGHT,
  TILE_WIDTH,
} from '../utils/constants.js';

/** The viewer's own rack. `seatPosition(localSeat, localSeat)` is 'bottom' by
 *  construction — routed through the helper anyway so POV placement has one
 *  rule, here and in the renderers. */
function localRack(layout: LayoutConfig, localSeat: Seat): SeatZone {
  return layout.seatZones[seatPosition(localSeat, localSeat)];
}

/**
 * The id of the local hand tile a tap at `(x, y)` selects for discard, or null
 * when the tap is inert (not the local seat's discard turn, or lands off the
 * rack).
 */
export function resolveDiscardFromTap(
  state: MahjongState,
  layout: LayoutConfig,
  x: number,
  y: number,
  localSeat: Seat,
): number | null {
  if (state.phase !== 'awaiting-discard' || state.turn !== localSeat) return null;
  const hand = sortTiles(state.hands[localSeat] ?? []);
  const index = hitTestHandTile({ x, y }, localRack(layout, localSeat), hand.length);
  if (index === null) return null;
  return hand[index]?.id ?? null;
}

export interface HoveredTile {
  readonly tile: Tile;
  /** True when this exact tile can be tapped right now (the local discard turn). */
  readonly usable: boolean;
}

const MELD_TILE_W = TILE_WIDTH * MELD_TILE_SCALE;
const MELD_TILE_H = TILE_HEIGHT * MELD_TILE_SCALE;

function isSideways(rotation: number): boolean {
  return rotation === 90 || rotation === 270;
}

/**
 * Any face-up tile under `screenPoint` — local hand tiles first (screen
 * space), then any seat's melds/discards (world space, only reachable when
 * `screenPoint` falls within the pond, matching the render clip). Opponents'
 * CONCEALED tiles are excluded — there is no face to name.
 */
export function findHoveredTile(
  state: MahjongState,
  layout: LayoutConfig,
  viewport: Viewport,
  screenPoint: Point,
  localSeat: Seat,
): HoveredTile | null {
  const localHand = sortTiles(state.hands[localSeat] ?? []);
  const handIndex = hitTestHandTile(screenPoint, localRack(layout, localSeat), localHand.length);
  if (handIndex !== null) {
    const tile = localHand[handIndex];
    if (tile) {
      const usable = state.phase === 'awaiting-discard' && state.turn === localSeat;
      return { tile, usable };
    }
  }

  const { pond } = layout;
  if (
    screenPoint.x < pond.x ||
    screenPoint.x > pond.x + pond.width ||
    screenPoint.y < pond.y ||
    screenPoint.y > pond.y + pond.height
  ) {
    return null;
  }
  const world = viewport.screenToWorld(screenPoint);

  for (const seat of SEATS) {
    const position = seatPosition(seat, localSeat);
    const sideways = isSideways(layout.seatZones[position].rotation);
    const { meldCell, discardCell } = splitPondCell(
      layout.pondSeats[position],
      position,
      MELD_STRIP_THICKNESS_PX,
      MELD_STRIP_GAP_PX,
    );

    const meldTiles = (state.melds[seat] ?? []).flatMap((meld) => meld.tiles);
    const meldIndex = hitTestMeldStrip(
      world,
      meldCell,
      meldTiles.length,
      MELD_TILE_W,
      MELD_TILE_H,
      MELD_TILE_GAP_PX,
      MELD_END_INSET_PX,
      sideways,
    );
    if (meldIndex !== null) {
      const tile = meldTiles[meldIndex];
      if (tile) return { tile, usable: false };
    }

    const pile = state.discards[seat] ?? [];
    const discardIndex = hitTestDiscardPile(
      world,
      discardCell,
      pile.length,
      DISCARD_TILE_GAP_PX,
      sideways,
      pondWrapFrom(position),
    );
    if (discardIndex !== null) {
      const tile = pile[discardIndex];
      if (tile) return { tile, usable: false };
    }
  }
  return null;
}
